import React, { useState, useEffect, useRef } from "react";
import { cachedGet, readCache } from "../api";
import styled from "styled-components";
import { Link, useHistory } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Home,
  BookOpen,
  User,
  LogOut,
} from "lucide-react";
import { useAmazonContext } from "../../Contexts/AmazonContext";
import BottomNavBar from "./BottomNavBar";
import LottieAnimation from "../LottieAnimation";
import StreakDisplay from "../StreakDisplay";
import LessonCountDisplay from "../LessonCountDisplay";
import HHLogo from "../sidebar/logo-without-tagline.svg";
import CourseImageSkeleton from "./CourseImageSkeleton";
import KnowledgeChallengeHomeButton from "../knowledgeChallenge/KnowledgeChallengeHomeButton";
import QuotesOfTheDay from "./QuotesOfTheDay";

import {
  Container,
  Header,
  ScrollContainer,
  BookCover,
  CoverImage,
  NavButton,
  NewRibbon,
} from "./landingPageSharedStyledComponents";

const CategoryContainer = styled.div`
  margin-bottom: 40px;
  position: relative;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 0 20px;
  gap: 10px;

  p {
    margin: 0;
    font-size: 1.8rem;
    font-weight: bold;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    min-width: 0;
  }

  @media only screen and (max-width: 600px) {
    padding: 0 15px;
    
    p {
      font-size: 1.4rem;
      font-weight: bold;
    }
  }
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
  overflow: hidden;
`;

const Logo = styled.img`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  background-color: #222222;
  border-radius: 8px;
  padding: 4px;

  @media only screen and (max-width: 600px) {
    width: 40px;
    height: 40px;
  }
`;

const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const StickyHeader = styled(Header)`
  position: sticky;
  top: 0;
  z-index: 100;
  background: white;
  border-bottom: 1px solid #e0e0e0;
  padding: 8px 0;
`;

const CategoryTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;

  margin: 0;
  margin-left: 2vh;
  padding: 10px 0;
  margin-bottom: 15px;
  position: relative;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: 0;
    width: 50px;
    height: 3px;
    background-color: #8747d4;
  }
`;

const CourseScrollContainer = styled(ScrollContainer)`
  margin-bottom: 20px;
  margin-left: 10px;
  padding-top: 5px;

  @media only screen and (min-width: 601px) {
    padding: 5px 40px 0;
  }
`;

const ScrollButton = styled.button`
  display: none;

  @media only screen and (min-width: 601px) {
    display: flex;
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background: rgba(255, 255, 255, 0.7);
    border: none;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    z-index: 1;
    transition: background 0.3s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.9);
    }

    &.left {
      left: 0;
    }

    &.right {
      right: 0;
    }
  }
`;

const LottieContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000;
  background-color: #fff;

  > div {
    width: 100%;
    max-width: 300px;

    @media only screen and (min-width: 601px) {
      max-width: 50%;
    }
  }
`;

const ComingSoonBanner = styled.div`
  margin-top: 40px;
  padding: 20px 0;
  background: #fc6722;
  border-radius: 15px;
  margin-left: 2vh;
  margin-right: 2vh;
  overflow: hidden;

  @media only screen and (min-width: 601px) {
    margin-left: 40px;
    margin-right: 40px;
  }
`;

const ComingSoonTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: white;
  margin: 0;
  margin-bottom: 15px;
  padding: 0 20px;
  display: flex;
  align-items: center;
  gap: 10px;

  &::after {
    content: "👀";
    font-size: 1.5rem;
  }
`;

const ComingSoonScrollContainer = styled(CourseScrollContainer)`
  margin-left: 0;
  margin-bottom: 0;
  padding: 15px 24px;
  scroll-padding-left: 24px;
  scroll-padding-right: 24px;

  /* Fade the leading/trailing edges so posters scroll in and out smoothly
     instead of being abruptly clipped by the banner edge. */
  mask-image: linear-gradient(
    to right,
    transparent 0,
    #000 24px,
    #000 calc(100% - 24px),
    transparent 100%
  );
  -webkit-mask-image: linear-gradient(
    to right,
    transparent 0,
    #000 24px,
    #000 calc(100% - 24px),
    transparent 100%
  );

  @media only screen and (min-width: 601px) {
    padding: 15px 32px;
    scroll-padding-left: 32px;
    scroll-padding-right: 32px;

    mask-image: linear-gradient(
      to right,
      transparent 0,
      #000 32px,
      #000 calc(100% - 32px),
      transparent 100%
    );
    -webkit-mask-image: linear-gradient(
      to right,
      transparent 0,
      #000 32px,
      #000 calc(100% - 32px),
      transparent 100%
    );
  }
`;

const TiltedBookCover = styled(BookCover)`
  transform: rotate(-5deg);
  transition: transform 0.3s ease;

  &:hover {
    transform: rotate(0deg);
  }
`;

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: ${150 * (2250 / 1410)}px;
  border-radius: 8px;
  overflow: hidden;
`;

const LoadErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  min-height: 70vh;
  padding: 0 24px;
  text-align: center;

  p {
    margin: 0;
    font-size: 1.1rem;
  }
`;

const RetryButton = styled.button`
  background-color: #8747d4;
  color: white;
  border: none;
  border-radius: 8px;
  padding: 10px 24px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
`;

// Delays before each automatic retry when the course list fails to load.
const RETRY_DELAYS = [1000, 3000];

// Sorts courses and buckets them by category, keeping "Coming Soon" as its own trailing
// group. Shared by the initial render (from cache) and the fetch handler.
function groupCoursesByCategory(courses) {
  const sortedCourses = [...courses].sort(
    (a, b) => a.courseNumber - b.courseNumber
  );

  // Group active courses by category (excluding Coming Soon)
  const groupedCourses = sortedCourses.reduce((acc, course) => {
    if (course.courseIsActive && course.courseCategory !== "Coming Soon") {
      const category = course.courseCategory;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(course);
    }
    return acc;
  }, {});

  // Get inactive courses that are marked as Coming Soon
  const comingSoonCourses = sortedCourses.filter(
    (course) => !course.courseIsActive && course.courseCategory === "Coming Soon"
  );

  if (comingSoonCourses.length > 0) {
    groupedCourses["Coming Soon"] = comingSoonCourses;
  }

  return groupedCourses;
}

function CourseHomePage() {
  const {
    userRole,
    progressData,
    setProgressData,
    chapterProgressData,
    setChapterProgressData,
    isDarkMode,
    setNavOpen,
    fetchUserStreak,
    fetchUserLessons,
  } = useAmazonContext();
  // Paint straight from cache when the course list has already been loaded this session.
  const cachedCourses = readCache("/coursesData", undefined, 300000);
  const [coursesByCategory, setCoursesByCategory] = useState(() =>
    cachedCourses ? groupCoursesByCategory(cachedCourses.data) : {}
  );
  const [showArrows, setShowArrows] = useState({});
  const [isLoading, setIsLoading] = useState(!cachedCourses);
  const [loadError, setLoadError] = useState(false);
  const [imageLoadingStates, setImageLoadingStates] = useState({});
  const history = useHistory();
  const scrollContainerRefs = useRef({});
  const hasCoursesRef = useRef(Boolean(cachedCourses));
  const loadCoursesRef = useRef(null);

  useEffect(() => {
    setNavOpen(false);
    
    const userEmail = localStorage.getItem("userEmail");
    if (userEmail) {
      fetchUserStreak();
      fetchUserLessons();
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    let retryTimer;
    let requestInFlight = false;

    const applyCourses = (courses) => {
      const groupedCourses = groupCoursesByCategory(courses);

      setCoursesByCategory(groupedCourses);
      
      // Mark images as loading, but never clobber one that already reported onLoad.
      // The covers can render before this runs (when the course list came from cache),
      // and their elements are unchanged, so the load event would not fire a second time.
      const allCourses = [
        ...Object.values(groupedCourses).flat(),
        ...(groupedCourses["Coming Soon"] || [])
      ];
      setImageLoadingStates((prev) => {
        const next = { ...prev };
        allCourses.forEach((course) => {
          if (next[course.id] === undefined) next[course.id] = true;
        });
        return next;
      });
      
      // Preload critical images (first 3 course images)
      const firstCategory = Object.values(groupedCourses)[0];
      if (firstCategory && firstCategory.length > 0) {
        const criticalImages = firstCategory.slice(0, 3).map(course => 
          convertToCloudFrontUrl(course.courseImage)
        );
        
        // Preload images using link prefetch
        criticalImages.forEach(imageUrl => {
          const link = document.createElement('link');
          link.rel = 'prefetch';
          link.href = imageUrl;
          link.as = 'image';
          document.head.appendChild(link);
        });
      }
      
      hasCoursesRef.current = true;
      setLoadError(false);
      setIsLoading(false);
    };

    const load = (attempt = 0) => {
      clearTimeout(retryTimer);
      requestInFlight = true;
      cachedGet(`/coursesData`, { ttl: 300000 })
        .then((courses) => {
          requestInFlight = false;
          if (cancelled) return;
          applyCourses(courses);
        })
        .catch((error) => {
          requestInFlight = false;
          // A failed background refresh is harmless when courses are already on screen.
          if (cancelled || hasCoursesRef.current) return;
          if (attempt < RETRY_DELAYS.length) {
            retryTimer = setTimeout(() => load(attempt + 1), RETRY_DELAYS[attempt]);
          } else {
            console.error("Error fetching courses:", error);
            setIsLoading(false);
            setLoadError(true);
          }
        });
    };
    loadCoursesRef.current = load;

    // Requests dropped while the app was backgrounded or offline won't recover on their
    // own, so try again as soon as the page is visible or the connection returns.
    const retryIfStuck = () => {
      if (cancelled || hasCoursesRef.current || requestInFlight) return;
      if (document.visibilityState === "hidden") return;
      load();
    };

    load();
    document.addEventListener("visibilitychange", retryIfStuck);
    window.addEventListener("online", retryIfStuck);

    return () => {
      cancelled = true;
      clearTimeout(retryTimer);
      document.removeEventListener("visibilitychange", retryIfStuck);
      window.removeEventListener("online", retryIfStuck);
    };
  }, []);

  const handleRetry = () => {
    setLoadError(false);
    setIsLoading(true);
    loadCoursesRef.current?.();
  };

  useEffect(() => {
    const checkScrollability = () => {
      Object.keys(coursesByCategory).forEach((category) => {
        const container = scrollContainerRefs.current[category];
        if (container) {
          const isScrollable = container.scrollWidth > container.clientWidth;
          setShowArrows((prev) => ({ ...prev, [category]: isScrollable }));
        }
      });
    };

    checkScrollability();
    window.addEventListener("resize", checkScrollability);

    return () => window.removeEventListener("resize", checkScrollability);
  }, [coursesByCategory]);

  const scroll = (direction, category) => {
    if (scrollContainerRefs.current[category]) {
      const scrollAmount = direction === "left" ? -160 : 160;
      scrollContainerRefs.current[category].scrollBy({
        left: scrollAmount,
        behavior: "smooth",
      });
    }
  };

  const convertToCloudFrontUrl = (s3Url) => {
    if (!s3Url) return s3Url;
    return s3Url.replace(
      "https://hhcourses-assets.s3.us-east-2.amazonaws.com",
      "https://d10jmd9bhq0s4v.cloudfront.net"
    );
  };

  // Returning the previous object unchanged lets React skip the re-render. That matters
  // because the ref guard below calls this on every render, which would otherwise loop.
  const settleImage = (courseId) => {
    setImageLoadingStates(prev =>
      prev[courseId] === false ? prev : { ...prev, [courseId]: false }
    );
  };

  const handleImageLoad = (courseId) => {
    settleImage(courseId);
  };

  const handleImageError = (courseId) => {
    settleImage(courseId);
  };

  const isImageLoading = (courseId) => {
    return imageLoadingStates[courseId] !== false;
  };

  if (isLoading) {
    return (
      <div>
        <LottieContainer>
          <LottieAnimation
            lottieFile="https://hhcourses-assets.s3.us-east-2.amazonaws.com/General/LottieFiles/loading.json"
            loop={true}
          ></LottieAnimation>
        </LottieContainer>
        <BottomNavBar></BottomNavBar>
      </div>
    );
  }

  if (loadError) {
    return (
      <div>
        <LoadErrorContainer>
          <p className={isDarkMode ? "darkThemeFont" : "lightThemeFont"}>
            Couldn't load courses. Check your connection and try again.
          </p>
          <RetryButton onClick={handleRetry}>Try again</RetryButton>
        </LoadErrorContainer>
        <BottomNavBar></BottomNavBar>
      </div>
    );
  }

  return (
    <div>
      <StickyHeader>
        <HeaderContent>
          <LeftSection>
            <Logo src={HHLogo} alt="HH Logo" />
        <p>HH Courses</p>
          </LeftSection>
          <RightSection>
            <LessonCountDisplay />
            <StreakDisplay />
          </RightSection>
        </HeaderContent>
      </StickyHeader>

      <KnowledgeChallengeHomeButton />

      <Container>
        {Object.entries(coursesByCategory)
          .filter(([category]) => category !== "Coming Soon")
          .map(([category, courses], categoryIndex) => (
            <CategoryContainer key={category}>
              <CategoryTitle
                className={isDarkMode ? "darkThemeFont" : "lightThemeFont"}
              >
                {category}
              </CategoryTitle>
              {showArrows[category] && (
                <ScrollButton
                  className="left"
                  onClick={() => scroll("left", category)}
                >
                  <ChevronLeft size={24} />
                </ScrollButton>
              )}
              <CourseScrollContainer
                ref={(el) => (scrollContainerRefs.current[category] = el)}
              >
                {courses.map((course, courseIndex) => {
                  // Load first 3 images eagerly (above the fold), rest lazily
                  const isAboveFold = categoryIndex === 0 && courseIndex < 3;
                  const imageLoading = isImageLoading(course.id);
                  
                  return (
                    <BookCover
                      key={course.id}
                      to={`/${course.courseTitle.replace(/\s/g, "")}`}
                    >
                      {course.courseIsNew && (
                        <NewRibbon
                          animate={{ rotate: [0, -4, 4, -2, 0] }}
                          transition={{
                            duration: 1,
                            repeat: Infinity,
                            repeatDelay: .2,
                            ease: "easeInOut",
                          }}
                        >
                          New!
                        </NewRibbon>
                      )}
                      <ImageContainer>
                        {imageLoading && <CourseImageSkeleton />}
                        <CoverImage
                          src={convertToCloudFrontUrl(course.courseImage)}
                          alt={`${course.title} cover`}
                          loading={isAboveFold ? "eager" : "lazy"}
                          fetchpriority={isAboveFold ? "high" : "auto"}
                          // A cached image can finish before the load handler is attached,
                          // in which case onLoad never fires and the skeleton would stick.
                          ref={(el) => {
                            if (el && el.complete && el.naturalWidth > 0) {
                              handleImageLoad(course.id);
                            }
                          }}
                          onLoad={() => handleImageLoad(course.id)}
                          onError={() => handleImageError(course.id)}
                          style={{ 
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            transition: 'opacity 0.3s ease-in-out',
                            opacity: imageLoading ? 0 : 1
                          }}
                        />
                      </ImageContainer>
                    </BookCover>
                  );
                })}
              </CourseScrollContainer>
              {showArrows[category] && (
                <ScrollButton
                  className="right"
                  onClick={() => scroll("right", category)}
                >
                  <ChevronRight size={24} />
                </ScrollButton>
              )}
            </CategoryContainer>
          ))}

        {coursesByCategory["Coming Soon"] && (
          <ComingSoonBanner>
            <ComingSoonTitle>Coming Soon</ComingSoonTitle>
            <ComingSoonScrollContainer
              ref={(el) => (scrollContainerRefs.current["Coming Soon"] = el)}
            >
              {coursesByCategory["Coming Soon"].map((course) => {
                const imageLoading = isImageLoading(course.id);
                
                return (
                  <TiltedBookCover
                    key={course.id}
                    to={`/${course.courseTitle.replace(/\s/g, "")}`}
                  >
                    <ImageContainer>
                      {imageLoading && <CourseImageSkeleton />}
                      <CoverImage
                        src={convertToCloudFrontUrl(course.courseImage)}
                        alt={`${course.title} cover`}
                        loading="lazy"
                        // A cached image can finish before the load handler is attached,
                        // in which case onLoad never fires and the skeleton would stick.
                        ref={(el) => {
                          if (el && el.complete && el.naturalWidth > 0) {
                            handleImageLoad(course.id);
                          }
                        }}
                        onLoad={() => handleImageLoad(course.id)}
                        onError={() => handleImageError(course.id)}
                        style={{ 
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          transition: 'opacity 0.3s ease-in-out',
                          opacity: imageLoading ? 0 : 1
                        }}
                      />
                    </ImageContainer>
                  </TiltedBookCover>
                );
              })}
            </ComingSoonScrollContainer>
          </ComingSoonBanner>
        )}

        <QuotesOfTheDay isDarkMode={isDarkMode} />
      </Container>
      <div style={{ minHeight: "100px" }}></div>
      <BottomNavBar></BottomNavBar>
    </div>
  );
}

export default CourseHomePage;
